import React, { useMemo } from 'react';

import {
  Edit,
  DateField,
  TabbedShowLayout,
  Tab,
  usePermissions,
  useRecordContext,
  Labeled,
  ListBase,
} from 'react-admin';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
} from '@mui/material';

import hasAccess from '../../utilities/hasAccess';

import LatestRelatedRequests from '../../customFields/LatestRelatedRequests';
import LatestRelatedCases from '../../customFields/LatestRelatedCases';
import LatestRelatedPings from '../../customFields/LatestRelatedPings';

import RiskManagement from '../riskManagment';
import AccountEntityBreadcrumb from './AccountEntityBreadcrumb';
import Transaction from '../transaction';

import actions, { Actions } from '../../constants/actions';
import { countryChoices } from '../../constants/countries';
import languages from '../../constants/languages';
import RelatedCustomConnections from '../connection/RelatedCustomConnections';
import RelatedIpAddresses from '../ipUsage/RelatedIpAddresses';
import { bankAccountTypesChoices } from '../../constants/bankAccountTypes';
import { bankAccountStatusesChoices } from '../../constants/bankAccountStatuses';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import EditArrayInput from '../../customFields/EditArrayInput';
import EditField from '../../customFields/EditField';
import EditSelectInput from '../../customFields/EditSelectInput';
import KycForm from '../kycForm';
import ReportModal from '../../customFields/ReportModal';
import reportTypes from '../../constants/reportTypes';
import { attributesSchema, checkIsTypeAllowed } from '../../utilities/reports/permissions';
import AccountHoldersList from './AccountHoldersList';
import ConnectedProducts from '../product/ConnectedProducts';
import PerformKyc from '../../customFields/PerformKyc';
import AuditResourceList from '../audit/AuditResourceList';
import EditToolbar from '../layout/EditToolbar';
import { ReportListDatagrid } from '../reports/ReportHistoryTable';

const EditActions = () => (
  <EditToolbar>
    <ReportModal />
  </EditToolbar>
);

export const AccountEntityEditContent = () => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();

  const showEdit = useMemo(
    () => hasAccess(permissions, actions.ACCOUNT_ENTITY_UPDATE),
    [permissions],
  );

  if (!record) return null;
  return (
    <TabbedShowLayout>
      <Tab label="Information">
        <Grid container spacing={6}>
          <Grid
            size={{
              xs: 12,
              lg: 5,
            }}
          >
            <Card variant="outlined">
              <CardHeader
                title="Account details"
                action={
                  hasAccess(permissions, actions.REQUEST_CREATE) && (<PerformKyc />)
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Box>
                      <Labeled label="Created at">
                        <DateField source="createdAt" />
                      </Labeled>
                    </Box>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="accountName" label="Account name" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditArrayInput showEdit={showEdit} source="customIds" label="Custom IDs" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="provider" label="Provider" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="iban" label="IBAN" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="bic" label="BIC" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditSelectInput
                      showEdit={showEdit}
                      source="country"
                      label="Country"
                      choices={countryChoices(languages.EN)}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="bban" label="BBAN" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="product" label="Product" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditSelectInput
                      showEdit={showEdit}
                      source="accountType"
                      label="Account type"
                      choices={bankAccountTypesChoices}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditSelectInput
                      showEdit={showEdit}
                      source="bankAccountStatus"
                      label="Bank account status"
                      choices={bankAccountStatusesChoices}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} type="date" source="opened" label="Opened date" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} type="date" source="closed" label="Closed date" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="bankgiroNumber" label="Bankgiro" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="plusgiroNumber" label="Plusgiro" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} type="boolean" source="nonBankingInstitution" label="Issued by non banking institution" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} source="source" label="Source" />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <EditField showEdit={showEdit} type="date" source="kycApprovedAt" label="KYC approved" />
                  </Grid>
                  <Grid size={12}>
                    <RiskManagement />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            container
            direction="column"
            size={{
              xs: 12,
              lg: 7,
            }}
          >
            <Grid>
              <AccountHoldersList />
            </Grid>
            <Grid>
              <RelatedCustomConnections />
            </Grid>
            <Grid>
              <RelatedIpAddresses />
            </Grid>
          </Grid>
          <LatestRelatedRequests />
          <LatestRelatedCases />
          <LatestRelatedPings />
        </Grid>
      </Tab>
      {hasAccess(permissions, actions.FORM_ANSWER_GET) && (
        <Tab label="KYC form" path="form-answers/*">
          <KycForm />
        </Tab>
      )}
      {hasAccess(permissions, actions.REQUEST_GET) && (
        <Tab label="Transactions" path="transactions">
          <Transaction />
        </Tab>
      )}
      {hasAccess(permissions, actions.PRODUCT_GET) && (
        <TabbedShowLayout.Tab label="Connected products" path="products">
          <ConnectedProducts />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
        <Tab label="Audit trail" path="audit-tracks">
          <AuditResourceList resourceType={resourceTypes.ACCOUNT_ENTITY} />
        </Tab>
      )}
      {hasAccess(permissions, actions.REPORT_LIST, {
        schema: attributesSchema,
        check: checkIsTypeAllowed(reportTypes.ENTITY_REPORT),
      }) && (
        <Tab label="Reports" path="reports">
          <ListBase
            storeKey={false}
            disableSyncWithLocation
            perPage={5}
            resource="reports"
            sort={{ field: 'createdAt', order: 'DESC' }}
            filter={{ type: reportTypes.ENTITY_REPORT, entityId: record.id }}
            queryOptions={{ refetchInterval: 5000 }}
          >
            <Card variant="outlined">
              <ReportListDatagrid />
            </Card>
          </ListBase>
        </Tab>
      )}
    </TabbedShowLayout>
  );
};

const AccountEntityEdit = () => {
  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      redirect={false}
      mutationMode="pessimistic"
      actions={<EditActions />}
      sx={{ '& .RaEdit-card': { marginTop: 0 } }}
    >
      <AccountEntityBreadcrumb />
      <AccountEntityEditContent />
    </Edit>
  );
};

export default AccountEntityEdit;
